import React, { FC, useEffect, useState } from 'react';
import { Icon, Box, Text, Mixins } from '@openfin/ui-library';
import styled from 'styled-components';
import { clamp } from 'lodash';

export type NumberSpinnerOnChange = (event: { name: string; value: number }) => void;

interface Props {
  min?: number;
  max?: number;
  value?: number;
  suffix?: string;
  onChange?: (value: number) => void;
  className?: string;
}

export const NumberSpinner: FC<Props> = ({
  onChange,
  value = 0,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  suffix = '',
  className = '',
}) => {
  const [inputValue, setInputValue] = useState(0);
  const displayValue = [inputValue.toString(), suffix].join(' ').trim();

  useEffect(() => {
    setInputValue(clamp(value, min, max));
  }, [value]);

  const handleClick = (inc: boolean) => {
    const val = clamp(inputValue + (inc ? 1 : -1), min, max);

    if (val !== inputValue) {
      setInputValue(val);

      if (onChange) {
        onChange(val);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'PageUp':
        {
          event.preventDefault();
          event.stopPropagation();
          let plusFive = inputValue + 5;
          if (plusFive > max) {
            plusFive = max;
          }
          setInputValue(plusFive);
        }
        break;
      case 'PageDown':
        {
          event.preventDefault();
          event.stopPropagation();
          let minusFive = inputValue - 5;
          if (minusFive < min) {
            minusFive = min;
          }
          setInputValue(minusFive);
        }
        break;
      case 'Home':
        event.preventDefault();
        event.stopPropagation();
        setInputValue(min);
        break;
      case 'End':
        event.preventDefault();
        event.stopPropagation();
        setInputValue(max);
        break;
      case 'ArrowDown':
        {
          event.preventDefault();
          event.stopPropagation();
          let minusOne = inputValue - 1;
          if (minusOne < min) {
            minusOne = min;
          }
          setInputValue(minusOne);
        }
        break;
      case 'ArrowUp':
        {
          event.preventDefault();
          event.stopPropagation();
          let plusOne = inputValue + 1;
          if (plusOne < min) {
            plusOne = min;
          }
          setInputValue(plusOne);
        }
        break;
    }
  };

  return (
    <Container
      role="group"
      aria-labelledby="spinner-label spinner-hidden"
      alignItems="center"
      justifyContent="space-between"
      gap="base"
      className={className}
      tabIndex={0}
      onKeyUp={handleKeyPress}
    >
      <VisuallyHidden id="spinner-hidden">
        How long a notification will display before disappearing.
      </VisuallyHidden>
      <IconButton
        role="button"
        icon="MinusIcon"
        aria-label="decrement"
        onClick={() => handleClick(false)}
      />
      <DisplayValue
        role="spinbutton"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={displayValue}
        size="large"
      >
        {displayValue}
      </DisplayValue>
      <IconButton
        role="button"
        icon="PlusIcon"
        aria-label="increment"
        onClick={() => handleClick(true)}
      />
    </Container>
  );
};

const Container = styled(Box)<{ 'aria-labelledby': string }>`
  position: relative;
  height: 28px;
  background-color: ${({ theme }) => theme.palette.background6};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: ${({ theme }) => theme.radius.small};
  border: 1px solid transparent;

  &:hover,
  &:focus-within {
    outline: none;
    border-color: ${({ theme }) => theme.palette.inputFocused};
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const VisuallyHidden = styled.div`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;
const IconButton = styled(Icon).attrs({ size: 'base' })`
  margin: 0 ${({ theme }) => theme.px.xsmall};
`;

const DisplayValue = styled(Text)`
  text-align: center;
  user-select: none;
  ${Mixins.textOverflow};
  min-width: ${({ theme }) => theme.px.xxxxlarge};
  width: ${({ theme }) => theme.px.xxxxlarge};
  overflow: unset;
`;
